import { Component, ReactNode } from "react";
import { container } from '../../context/module';
import { MetricsService } from '../../services/metrics.service';
import { MetricEventName } from '../../types/metric-events';
import { Subscription } from "rxjs";
import { withRequestIdleCallbackAsync } from "../../annotations/request-idle-callback.annotation";
import { gifEstimateStore } from "../../utils/gif-estimate.store";
import { FileUpload } from "../../models/file-upload";
import { UploadInProgressFile } from "../../models/upload-in-progress-file";
import Checkbox from "../ui/checkbox/checkbox.component";
import Dropdown from "../ui/dropdown/dropdown";
import Seekbar from "../ui/seekbar/seekbar.component";
import styles from './convert-options.component.module.scss';
import { ConvertOptionsContextV2, ConvertOptionsSummary } from "./convert-options.context";
import { BooleanViewState, DropdownOptionViewState, DropdownViewState, GroupViewState, Note, OptionLevel, OptionViewState, RangeViewState, RootOptionViewState, SelectableOptionViewState, SubgroupViewState, TextInputDropdownViewState, TextInputViewState, ValueViewState, ViewState } from "./convert-options.view-states";
import { isHidden } from "./models/option-requirement";
import { DropdownInputComponent, DropdownInputValueChangeEvent } from "./widgets/dropdown-input.component";
import { TextInputComponent, TextInputValueChangeEvent } from "./widgets/text-input.component";
interface ConvertOptionsComponentState {
    results: any;
}

interface ConvertOptionsComponentProps {
    convertOptionsContext: ConvertOptionsContextV2;
    fileUpload?: FileUpload;
    uploadInProgressFiles: UploadInProgressFile<any>[];
    onOptionChange: (data: ConvertOptionsSummary) => void;
    showAdvanced: boolean;
    path: string;
}

export class ConvertOptionsComponentV2 extends Component<ConvertOptionsComponentProps, ConvertOptionsComponentState> {

    private model: RootOptionViewState;
    private modelChangeSubscription?: Subscription;
    private unsubscribeGifEstimates?: () => void;

    constructor(props) {
        super(props);
        this.model = this.props.convertOptionsContext.getModel() as any;

        // This calculates the initial view state including hidden options (ensureContextState)
        // preventing CLS by ensuring the first render matches the final state.
        const initialResults = this.model ? this.props.convertOptionsContext.getSummary() : null;

        this.state = {
            results: initialResults
        };
    }

    private prevSummarySnapshot: { [key: string]: string } | null = null;

    /**
     * "Which knobs do users actually touch" (sweep P2). The model observer emits the
     * whole summary, so changed keys come from a shallow diff against the previous
     * emission. Sampled 1:10 client-side; the always-counted side effect feeds
     * MetricsService.optionChangesThisPage so CONVERT_START can report it exactly.
     */
    private emitOptionChangeMetric(summary: { [key: string]: any }) {
        try {
            const snap: { [key: string]: string } = {};
            for (const k of Object.keys(summary || {})) snap[k] = JSON.stringify(summary[k]);
            const prev = this.prevSummarySnapshot;
            this.prevSummarySnapshot = snap;
            if (!prev) return; // first emission is the baseline, not a user change
            const metrics = container.get(MetricsService);
            for (const k of Object.keys(snap)) {
                if (prev[k] === snap[k]) continue;
                metrics.optionChangesThisPage += 1;
                if (Math.random() < 0.1) {
                    metrics.emit(MetricEventName.OPTION_CHANGED, {
                        key: k,
                        page: typeof location !== 'undefined' ? location.pathname : '',
                    });
                }
            }
        } catch (e) { /* metrics must never break the options panel */ }
    }

    componentDidMount(): void {
        this.updateLikelyStaleResult();

        this.modelChangeSubscription = this.props.convertOptionsContext.modelChangeObserver.subscribe({
            next: (obj) => {
                this.updateLikelyStaleResult();
                this.emitOptionChangeMetric(obj);
                this.props.onOptionChange(obj);
            }
        });
        // Estimates land asynchronously (Stage 0/1/2) and only re-derive NOTES,
        // never values — re-render so the textFn notes read the fresh records.
        this.unsubscribeGifEstimates = gifEstimateStore.subscribe(() => this.forceUpdate());
    }

    componentWillUnmount(): void {
        this.modelChangeSubscription?.unsubscribe();
        this.unsubscribeGifEstimates?.();
    }

    componentDidUpdate(prevProps: Readonly<ConvertOptionsComponentProps>, prevState: Readonly<ConvertOptionsComponentState>, snapshot?: any): void {
        this.updateLikelyStaleResult();
    }

    updateLikelyStaleResult() {
        if (this.model) {
            // Trigger restoration logic (Mutates model if context changed)
            // We use getSummary() because it effectively calls summarize() with the current view results
            this.props.convertOptionsContext.getSummary();

            const currentResults = this.model.getResultForView();
            // Simple JSON stringify comparison to avoid infinite loop
            if (JSON.stringify(currentResults) !== JSON.stringify(this.state.results)) {
                this.setState({
                    results: currentResults
                });
            }
        }
    }

    render(): ReactNode {
        return <div>
            {
                this.model && this.model.options
                    .filter(it => it instanceof GroupViewState)
                    .filter(currentVS => {
                        if (!this.props.showAdvanced && currentVS.optionLevel === OptionLevel.Advanced) {
                            return false;
                        }

                        return !currentVS.isHidden(this.state.results, (propertyName: string) => {
                            const viewStates = this.props.convertOptionsContext.getViewStates(propertyName);
                            if (!viewStates) {
                                return false;
                            }
                            const shouldBeHidden = viewStates.filter(it => {
                                return it instanceof RootOptionViewState ||
                                    it instanceof GroupViewState ||
                                    it instanceof SubgroupViewState;
                            }).filter(it => it != currentVS).every(it => {
                                let vs: ViewState = it;
                                if (vs instanceof RootOptionViewState) {
                                    return false;
                                }
                                while (!(vs instanceof RootOptionViewState)) {
                                    if (vs != currentVS && (vs as any)?.isHidden && (vs as any)?.isHidden(this.state.results)) {
                                        return true;
                                    }
                                    vs = vs.parent;
                                }
                                return false
                            });
                            return shouldBeHidden;
                        })
                    })
                    .map(subgroup => {
                        const content = this.renderGroup(subgroup);
                        if (!content) {
                            return null;
                        }
                        return <div className="group" key={`group-parent-${subgroup.id}`}>
                            {
                                content
                            }
                        </div>
                    })
            }
        </div>
    }

    renderGroup(model: GroupViewState) {
        const selectableGroupOptions = model.options;
        const visibleOptions = selectableGroupOptions
            .filter(it => !it.isHidden(this.state.results))
            .filter(it => this.props.showAdvanced || it.optionLevel !== OptionLevel.Advanced);

        if (visibleOptions.length === 0) {
            return null;
        }

        if (visibleOptions.length === 1) {
            const singleOption = visibleOptions[0];
            const hasChildren = singleOption.options && singleOption.options.length > 0;

            if (!hasChildren) {
                return null;
            }

            const selectedSubgroupOption = singleOption.options?.filter(it => it instanceof SubgroupViewState);
            return <div className={styles['group']}>
                <div className={styles['header']}>{model.name}</div>
                {this.renderNotes(model.notes, styles['category-notes'])}
                <div className={styles['category_option_selections-and-sub-options']}>
                    <div>
                        {this.renderNotes(singleOption.notes, styles['option-notes'])}
                        {
                            this.renderSubgroup(selectedSubgroupOption)
                        }
                    </div>
                </div>
            </div>
        }

        const selectedOption = selectableGroupOptions
            ?.find(it => it.selected)
            ?.options ?? selectableGroupOptions[0].options;

        const selectedSubgroupOption =
            selectedOption?.filter(it => it instanceof SubgroupViewState)
        return <div className={styles['group']}>
            <div className={styles['header']}>{model.name}</div>
            <div className={styles['category_option_selections-and-sub-options']}>
                <div>
                    {
                        this.renderGroupOptionsSection(selectableGroupOptions, model)
                    }
                </div>
                {this.renderNotes(model.notes, styles['category-notes'])}
                <div>
                    {
                        this.renderSubgroup(selectedSubgroupOption)
                    }
                </div>
                {
                    // A note on the selected MODE describes the outcome of every
                    // control the mode reveals, so it renders AFTER them — the
                    // house convention everywhere else (a note follows the thing
                    // it explains). No emitted tree carried mode notes before
                    // the gif verdict note, so nothing else moves.
                    this.renderNotes(
                        selectableGroupOptions.find(it => it.selected)?.notes,
                        styles['option-notes'],
                    )
                }
            </div>
        </div>
    }

    renderGroupOptionsSection(selectableGroupOptions: SelectableOptionViewState[], model: GroupViewState) {
        const visibleOptions = selectableGroupOptions
            .filter(it => !it.isHidden(this.state.results))
            .filter(it => this.props.showAdvanced || it.optionLevel !== OptionLevel.Advanced);

        if (visibleOptions.length > 6) {
            const dropdown = model;
            const values = visibleOptions;

            if (values.length === 0) {
                return null;
            }

            const selectedIsValid = values.find(it => it.selected);
            if (!selectedIsValid) {
                values[0].selected = true;
            }
            const selectedOption = dropdown.selected;
            return <div className={styles['category_option_selections-sub-options']}>
                <div className={`${styles['category_option_selections-sub-option']}`}>
                    <div className={styles['controller-container']}>
                        <Dropdown
                            key={`${model.id}-selectable-options`}
                            initialValue={selectedOption}
                            values={values}
                            label={null}
                            valueFormatter={(v) =>
                                v && v.name
                            }
                            hasTooltip={(v) => false}
                            getTooltip={(v) => "n/a"}
                            emitInitialValueOnInit={true}
                            searchable={true}
                            onSelect={(selectedValue, forced) =>
                                this.setSelectableOptionValue(
                                    dropdown,
                                    selectedValue
                                )
                            }
                        >
                        </Dropdown>
                    </div>
                </div>

            </div>
        }

        if (visibleOptions.length === 0) {
            return null;
        }

        const selectedOption = selectableGroupOptions.find(it => it.selected);

        return <div className={styles['category_option_selections-container']}>
            <div className={styles['category_option_selections']}>
                {visibleOptions
                    .map(child => {
                        return this.renderGroupOption(child, model);
                    })
                }
            </div>
        </div>
    }

    renderGroupOption(model: SelectableOptionViewState, parent: GroupViewState) {
        return <div className={styles['category_option_selection']} key={`group-option-${model.id}`}>
            <button
                className={`btn btn-light btn-gray-outline x-btn-svg x-btn-stacked ${styles[model.selected ? 'selected' : 'not-selected']}`}
                onClick={e => { this.setSelectableOptionValue(parent, model) }}>
                {model.iconPath && <img src={model.iconPath} />}
                {model.name}
            </button>
        </div>
    }

    renderSubgroup(model: SubgroupViewState[]) {
        const visibleSubgroups = model.filter(it => !it.isHidden(this.state.results));
        if (visibleSubgroups.length === 0) {
            return null;
        }
        return <div className={styles['category_option_selections-sub-options']}>
            {
                visibleSubgroups.map(it => {
                    return <div key={it.uuid}>
                        <div className={`${styles['category_option_selections-sub-option']}`}>
                            <div className={`${styles['label']} text-flex-vertical-center`}>
                                {it.name}
                            </div>
                            <div className={styles['controller-container']}>
                                <div>
                                    {
                                        it.options
                                            ?.filter(i => {
                                                return !(i instanceof GroupViewState || i instanceof SelectableOptionViewState)
                                            })
                                            ?.map(child => {
                                                return this.renderNonGroupOption(child, it);
                                            })
                                    }
                                </div>
                            </div>
                        </div>
                        {
                            this.renderNotes(it.notes, styles['suboption-notes'])
                        }
                    </div>
                })
            }

        </div>
    }

    renderNotes(notes: Note[], className: string) {
        if (!notes || notes.length === 0) return null;
        return notes.map((note, index) => {
            if (note.hidden && isHidden(note.hidden, this.state.results)) {
                return null;
            }
            if (note.hiddenFn && this.evalInContext(note.hiddenFn)) {
                return null;
            }
            return <div key={index}>
                {note.text && <div className={`${note.textClassName} ${className}`}
                    dangerouslySetInnerHTML={{
                        __html: this.evalInContext.call(
                            this,
                            note.text
                        )
                    }}>
                </div>}
                {note.textFn && Note.fn[note.textFn] && (() => {
                    // A note function returning nothing means "no note right now" —
                    // don't render an empty alert shell.
                    const html = Note.fn[note.textFn]({
                        results: this.state.results,
                        uploadInProgressFiles: this.props.uploadInProgressFiles,
                        context: this.props.convertOptionsContext,
                        fileUpload: this.props.fileUpload,
                        showAdvanced: this.props.showAdvanced,
                    });
                    if (!html) return null;
                    return <div className={`${note.textClassName} ${className}`}
                        dangerouslySetInnerHTML={{ __html: html }}>
                    </div>;
                })()}
            </div>
        });
    }

    evalInContext(value) {
        const results = this.state.results;
        const uploadInProgressFiles = this.props.uploadInProgressFiles;
        const context = this.props.convertOptionsContext;
        const fileUpload = this.props.fileUpload;
        const showAdvanced = this.props.showAdvanced;
        const path = this.props.path;
        return eval(value);
    }

    setSelectableOptionValue(parent: GroupViewState, model: SelectableOptionViewState) {
        parent.updateValue(model);
        this.props.convertOptionsContext.emitModelChanged();
    }

    @withRequestIdleCallbackAsync
    async setCheckboxValue(value: boolean, model: BooleanViewState) {
        model.updateValue(value);
        this.props.convertOptionsContext.emitModelChanged();
    }

    setSeekbarValue(value: number, model: RangeViewState) {
        model.updateValue(value);
        this.props.convertOptionsContext.emitModelChanged();
    }

    setDropdownInputValue(value: DropdownInputValueChangeEvent, model: TextInputDropdownViewState): void {
        model.updateValue(
            value.inputValue,
            value.dropdownValue
        );
        this.props.convertOptionsContext.emitModelChanged();
    }

    setTextInputValue(value: TextInputValueChangeEvent, model: TextInputViewState): void {
        model.updateValue(
            value.inputValue
        );
        this.props.convertOptionsContext.emitModelChanged();
    }

    setDropdownValue(
        value: DropdownViewState,
        selectedValue: DropdownOptionViewState,
        force: Boolean
    ): void {
        value.updateValue(selectedValue);
        this.props.convertOptionsContext.emitModelChanged();
    }

    renderNonGroupOption(model: OptionViewState | ValueViewState, patent: SubgroupViewState) {
        if (model instanceof BooleanViewState) {
            return <Checkbox
                key={`${patent.id}-${model.id}`}
                initialValue={model.value}
                ariaLabel={model?.name}
                onChange={(value) => {
                    this.setCheckboxValue(value, model);
                }}
                emitInitialValueOnInit={true}
            ></Checkbox>
        } else if (model instanceof RangeViewState) {
            return <Seekbar
                key={`${patent.id}-${model.id}`}
                step={model.step ?? 1}
                min={model.min}
                max={model.max}
                start={model.value}
                onChange={(value) => {
                    this.setSeekbarValue(value, model)
                }}
                emitInitialValueOnInit={true}
            ></Seekbar>
        } else if (model instanceof TextInputDropdownViewState) {
            const dropdown = model.dropdown;
            const values = dropdown.options.filter(it => !it.isHidden(this.state.results));
            const selectedIsValid = values.find(it => it.selected);
            if (!selectedIsValid) {
                dropdown.selected = values[0];
            }
            return <DropdownInputComponent
                key={`${patent.id}-${model.id}`}
                label={model.name}
                values={values}
                inputValue={model.value}
                dropdownValue={dropdown.selected}
                valueFormatter={(a) =>
                    a && a.name
                }
                valueChanged={(value) => this.setDropdownInputValue(value, model)} >
            </DropdownInputComponent>
        } else if (model instanceof DropdownViewState) {
            const dropdown = model;
            const values = dropdown.options.filter(it => !it.isHidden(this.state.results));
            const selectedIsValid = values.find(it => it.selected);
            if (!selectedIsValid) {
                dropdown.selected = values[0];
            }
            const selectedOption = dropdown.selected;
            return <div key={`${patent.id}-${model.id}`}>
                <Dropdown
                    initialValue={selectedOption}
                    values={values}
                    label={dropdown.name}
                    valueFormatter={(v) =>
                        v && v.name
                    }
                    hasTooltip={(v) => false}
                    getTooltip={(v) => "n/a"}
                    emitInitialValueOnInit={true}
                    onSelect={(selectedValue, forced) =>
                        this.setDropdownValue(
                            dropdown, selectedValue, forced
                        )
                    }
                >
                </Dropdown>
            </div>
        } else if (model instanceof TextInputViewState) {
            return <TextInputComponent
                key={`${patent.id}-${model.id}`}
                label={model.name}
                inputValue={model.value}
                valueChanged={(value) => this.setTextInputValue(value, model)}
            ></TextInputComponent>
        }
        return <div>{/* Orphan: {model.id} */}</div>
    }
}
